package br.com.heyagenda.data.repository

import br.com.heyagenda.data.remote.ExpenseDto
import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreExpenseDataSource
import br.com.heyagenda.domain.model.CalendarUtils
import br.com.heyagenda.domain.model.CurrencyUtils
import br.com.heyagenda.domain.model.Expense
import br.com.heyagenda.domain.model.ExpenseCategory
import br.com.heyagenda.domain.repository.ExpenseRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class ExpenseRepositoryImpl(
    private val firestoreDataSource: FirestoreExpenseDataSource,
    private val authDataSource: FirebaseAuthDataSource
) : ExpenseRepository {

    private val localExpenses = MutableStateFlow<List<Expense>>(emptyList())

    override fun getExpenses(): Flow<List<Expense>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            return localExpenses.asStateFlow()
        }

        return firestoreDataSource.observeExpenses(uid).map { dtos ->
            dtos.map { dto -> dto.toDomain() }
        }
    }

    override fun getExpensesInRange(startDateIso: String, endDateIso: String): Flow<List<Expense>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            return localExpenses.map { list -> list.filter { it.date in startDateIso..endDateIso } }
        }

        return firestoreDataSource.observeExpensesInRange(uid, startDateIso, endDateIso).map { dtos ->
            dtos.map { dto -> dto.toDomain() }
        }
    }

    override suspend fun addExpense(
        description: String,
        amount: Double,
        date: String,
        category: ExpenseCategory
    ): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        val finalDate = date.ifBlank { CalendarUtils.getTodayIso() }
        val priceFormatted = CurrencyUtils.format(amount)
        val dto = ExpenseDto(
            id = "",
            description = description.trim(),
            amount = amount,
            priceFormatted = priceFormatted,
            date = finalDate,
            category = category.name,
            createdAtIso = CalendarUtils.getTodayIso()
        )

        if (uid != null) {
            val result = firestoreDataSource.saveExpense(uid, dto)
            if (result.isSuccess) {
                return result
            }
        }

        // Fallback in memory
        val local = dto.copy(id = "local_exp_${localExpenses.value.size + 1}").toDomain()
        localExpenses.update { it + local }
        return Result.success(Unit)
    }

    override suspend fun deleteExpense(id: String): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val result = firestoreDataSource.deleteExpense(uid, id)
            if (result.isSuccess) {
                return result
            }
        }

        localExpenses.update { list -> list.filter { it.id != id } }
        return Result.success(Unit)
    }

    private fun ExpenseDto.toDomain(): Expense {
        val cat = try {
            ExpenseCategory.valueOf(category)
        } catch (_: Throwable) {
            ExpenseCategory.OUTROS
        }

        return Expense(
            id = id,
            description = description,
            amount = amount,
            priceFormatted = priceFormatted.ifBlank { CurrencyUtils.format(amount) },
            date = date,
            category = cat,
            createdAtIso = createdAtIso
        )
    }
}
